import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LpAdvancedTrade from './LpAdvancedTrade';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const STORAGE_WATCHLIST = 'predict_lp_bsc_watchlist_v1';
const STORAGE_OPTS = 'predict_lp_bsc_opts_v3';
const DEFAULT_TOKEN = '0xfe189e97832da1573e4e4ff034f4ffc3a15c7777';

const IL_SCENARIOS = [
  { label: '-50%', move: -0.5 },
  { label: '-20%', move: -0.2 },
  { label: '-10%', move: -0.1 },
  { label: '-5%', move: -0.05 },
  { label: '+5%', move: 0.05 },
  { label: '+10%', move: 0.1 },
  { label: '+20%', move: 0.2 },
  { label: '+50%', move: 0.5 },
];

function normalizeAddress(input) {
  const raw = String(input || '').trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(raw)) return '';
  return raw.toLowerCase();
}

function shortAddress(addr) {
  const a = String(addr || '');
  if (a.length < 12) return a || '-';
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function formatUsd(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function formatPct(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${(n * 100).toFixed(digits)}%`;
}

function formatApr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n >= 10) return `${(n * 100).toFixed(0)}%`;
  if (n >= 1) return `${(n * 100).toFixed(1)}%`;
  return `${(n * 100).toFixed(2)}%`;
}

function formatMoney(value, digits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1) return `$${n.toFixed(2)}`;
  if (Math.abs(n) >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

/** V2 经典无偿损失：k = P1/P0，IL = 2√k/(1+k) - 1 */
function impermanentLoss(priceMove) {
  const k = 1 + Number(priceMove);
  if (!(k > 0)) return null;
  return (2 * Math.sqrt(k)) / (1 + k) - 1;
}

function loadWatchlist() {
  try {
    const raw = localStorage.getItem(STORAGE_WATCHLIST);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) {
      return parsed
        .map((item) => ({
          address: normalizeAddress(item.address || item),
          label: String(item.label || item.symbol || '').trim(),
        }))
        .filter((item) => item.address);
    }
  } catch (_) {}
  return [{ address: DEFAULT_TOKEN, label: 'MarsCoin' }];
}

function saveWatchlist(list) {
  try {
    localStorage.setItem(STORAGE_WATCHLIST, JSON.stringify(list));
  } catch (_) {}
}

function loadOpts() {
  try {
    const raw = localStorage.getItem(STORAGE_OPTS);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      const dexFilter = ['all', 'major', 'pancake', 'uniswap'].includes(parsed.dexFilter)
        ? parsed.dexFilter
        : (parsed.pancakeOnly ? 'pancake' : 'major');
      return {
        capital: Number(parsed.capital) > 0 ? Number(parsed.capital) : 1000,
        refreshSec: [15, 30, 60].includes(Number(parsed.refreshSec)) ? Number(parsed.refreshSec) : 30,
        dexFilter,
        entryFilter: ['all', 'dual', 'single'].includes(parsed.entryFilter) ? parsed.entryFilter : 'all',
        minLiquidity: Number(parsed.minLiquidity) >= 0 ? Number(parsed.minLiquidity) : 1000,
        sortBy: parsed.sortBy === 'liquidity' || parsed.sortBy === 'volume' ? parsed.sortBy : 'apr',
      };
    }
  } catch (_) {}
  return {
    capital: 1000,
    refreshSec: 30,
    dexFilter: 'major',
    entryFilter: 'all',
    minLiquidity: 1000,
    sortBy: 'apr',
  };
}

function saveOpts(opts) {
  try {
    localStorage.setItem(STORAGE_OPTS, JSON.stringify(opts));
  } catch (_) {}
}

function yieldOnCapital(feeApr, capital, hours) {
  const apr = Number(feeApr);
  const cap = Number(capital);
  if (!Number.isFinite(apr) || !Number.isFinite(cap) || cap <= 0) return null;
  return cap * apr / 365 / 24 * hours;
}

/** V2=双币全区间；V3=同一双币池，但开仓可双币或单边 */
function getEntryMode(pair) {
  if (String(pair?.version || '').toLowerCase() === 'v3') {
    return {
      id: 'single-capable',
      label: '可单边',
      short: '单/双',
      detail: 'V3 双币池：可按市价两边加，也可只放一种币（区间在现价一侧）',
    };
  }
  return {
    id: 'dual',
    label: '双币全区间',
    short: '双币',
    detail: 'V2 全区间：通常需按池子比例同时投入两种币',
  };
}

function enrichPair(pair) {
  if (!pair) return null;
  return { ...pair, entryMode: getEntryMode(pair) };
}

export default function LpArbPage() {
  const [watchlist, setWatchlist] = useState(() => loadWatchlist());
  const [activeToken, setActiveToken] = useState(() => loadWatchlist()[0]?.address || DEFAULT_TOKEN);
  const [addInput, setAddInput] = useState('');
  const [opts, setOpts] = useState(() => loadOpts());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedPair, setSelectedPair] = useState('');
  const [cacheByToken, setCacheByToken] = useState({});
  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return localStorage.getItem('predict_lp_bsc_guide_open') !== '0';
    } catch {
      return true;
    }
  });

  const updateOpts = useCallback((patch) => {
    setOpts((prev) => {
      const next = { ...prev, ...patch };
      saveOpts(next);
      return next;
    });
  }, []);

  const loadToken = useCallback(async (token, silent = false) => {
    const addr = normalizeAddress(token);
    if (!addr) {
      setMessage('请输入有效合约地址');
      return;
    }
    if (!silent) setLoading(true);
    try {
      const resp = await fetch(api(`/api/lp-bsc?token=${encodeURIComponent(addr)}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || body?.data?.error || `加载失败 ${resp.status}`);
      const next = body.data || null;
      setCacheByToken((prev) => ({ ...prev, [addr]: next }));
      if (addr === normalizeAddress(activeToken)) {
        setData(next);
        setMessage('');
        setWatchlist((prev) => {
          const updated = prev.map((item) => (
            item.address === addr
              ? { ...item, label: next?.tokenSymbol || item.label || shortAddress(addr) }
              : item
          ));
          saveWatchlist(updated);
          return updated;
        });
        const first = next?.pairs?.[0]?.pairAddress || '';
        setSelectedPair((prev) => {
          if (prev && next?.pairs?.some((p) => p.pairAddress === prev)) return prev;
          return first;
        });
      }
    } catch (err) {
      if (addr === normalizeAddress(activeToken)) {
        setMessage(err?.message || 'BSC LP 数据读取失败');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeToken]);

  useEffect(() => {
    loadToken(activeToken);
  }, [activeToken, loadToken]);

  useEffect(() => {
    const sec = Number(opts.refreshSec) || 30;
    const timer = window.setInterval(() => {
      loadToken(activeToken, true);
    }, sec * 1000);
    return () => window.clearInterval(timer);
  }, [activeToken, opts.refreshSec, loadToken]);

  function addToken() {
    const addr = normalizeAddress(addInput);
    if (!addr) {
      setMessage('合约地址格式不正确');
      return;
    }
    setWatchlist((prev) => {
      if (prev.some((item) => item.address === addr)) return prev;
      const next = [...prev, { address: addr, label: shortAddress(addr) }];
      saveWatchlist(next);
      return next;
    });
    setActiveToken(addr);
    setAddInput('');
  }

  function removeToken(address) {
    setWatchlist((prev) => {
      const next = prev.filter((item) => item.address !== address);
      const fallback = next.length ? next : [{ address: DEFAULT_TOKEN, label: 'MarsCoin' }];
      saveWatchlist(fallback);
      if (address === activeToken) setActiveToken(fallback[0].address);
      return fallback;
    });
  }

  const filteredPairs = useMemo(() => {
    const list = (Array.isArray(data?.pairs) ? data.pairs : []).map(enrichPair);
    let out = list.filter((item) => Number(item.liquidityUsd || 0) >= Number(opts.minLiquidity || 0));
    if (opts.dexFilter === 'pancake') {
      out = out.filter((item) => (item.dexBrand?.id || item.dexId || '').toLowerCase().includes('pancake'));
    } else if (opts.dexFilter === 'uniswap') {
      out = out.filter((item) => (item.dexBrand?.id || item.dexId || '').toLowerCase().includes('uniswap'));
    } else if (opts.dexFilter === 'major') {
      out = out.filter((item) => {
        const id = (item.dexBrand?.id || item.dexId || '').toLowerCase();
        return id.includes('pancake') || id.includes('uniswap');
      });
    }
    if (opts.entryFilter === 'dual') {
      out = out.filter((item) => item.entryMode?.id === 'dual');
    } else if (opts.entryFilter === 'single') {
      out = out.filter((item) => item.entryMode?.id === 'single-capable');
    }
    out = [...out].sort((a, b) => {
      if (opts.sortBy === 'liquidity') return Number(b.liquidityUsd || 0) - Number(a.liquidityUsd || 0);
      if (opts.sortBy === 'volume') return Number(b.volume24h || 0) - Number(a.volume24h || 0);
      return Number(b.feeApr || 0) - Number(a.feeApr || 0);
    });
    return out;
  }, [data, opts.minLiquidity, opts.dexFilter, opts.entryFilter, opts.sortBy]);

  const dualPools = useMemo(
    () => filteredPairs.filter((item) => item.entryMode?.id === 'dual'),
    [filteredPairs],
  );
  const singleCapablePools = useMemo(
    () => filteredPairs.filter((item) => item.entryMode?.id === 'single-capable'),
    [filteredPairs],
  );

  const strategyCards = useMemo(() => {
    const fromApi = Array.isArray(data?.strategies) ? data.strategies : [];
    const byPair = new Map(filteredPairs.map((item) => [item.pairAddress, item]));
    const cards = [];
    const seen = new Set();
    for (const item of fromApi) {
      const live = byPair.get(item.pairAddress);
      if (!live) continue;
      if (seen.has(live.pairAddress)) continue;
      seen.add(live.pairAddress);
      cards.push(live);
    }
    for (const item of filteredPairs) {
      if (seen.has(item.pairAddress)) continue;
      seen.add(item.pairAddress);
      cards.push(item);
      if (cards.length >= 8) break;
    }
    return cards.slice(0, 8);
  }, [data, filteredPairs]);

  const activePair = useMemo(
    () => filteredPairs.find((item) => item.pairAddress === selectedPair) || filteredPairs[0] || null,
    [filteredPairs, selectedPair],
  );

  const updatedAt = data?.syncedAt
    ? new Date(data.syncedAt).toLocaleTimeString('zh-CN', { hour12: false })
    : '-';

  const bestApr = strategyCards[0]?.feeApr;
  const best1h = yieldOnCapital(bestApr, opts.capital, 1);

  function toggleGuide() {
    setGuideOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('predict_lp_bsc_guide_open', next ? '1' : '0');
      } catch (_) {}
      return next;
    });
  }

  const guideDex = activePair?.dexBrand?.id === 'uniswap' ? 'uniswap' : 'pancake';
  const guideIsV3 = activePair?.version === 'v3';
  const guidePairLabel = activePair?.pairLabel || 'TOKEN/USDT';
  const guideFee = Number.isFinite(Number(activePair?.feeRatePct))
    ? `${Number(activePair.feeRatePct).toFixed(2)}%`
    : '0.25%~1%';
  const guideDexName = activePair?.dexBrand?.name || (guideDex === 'uniswap' ? 'Uniswap' : 'PancakeSwap');
  const guideAddUrl = guideDex === 'uniswap'
    ? 'https://app.uniswap.org/positions/create?chain=bnb'
    : (guideIsV3
      ? 'https://pancakeswap.finance/v3/add'
      : 'https://pancakeswap.finance/v2/add');
  const guidePoolUrl = activePair?.url || null;

  return (
    <div className="lp-arb-page">
      <section className="lp-arb-hero">
        <div>
          <span className="lp-arb-eyebrow">BSC LP Fee Monitor</span>
          <h1>LP 流动套利</h1>
          <p>
            按合约地址扫描 BSC 池子（含 Pancake 与 Uniswap 独角兽），估算手续费年化与约每小时收益，并提示无偿损失情景。
            收益为研究估算，不构成投资建议；未扣 Gas、滑点与脱区间损失。
          </p>
        </div>
        <div className="lp-arb-hero-side">
          <label>
            投入本金（USD）
            <input
              type="number"
              min="1"
              step="100"
              value={opts.capital}
              onChange={(event) => updateOpts({ capital: Number(event.target.value) || 1000 })}
            />
          </label>
          <button type="button" onClick={() => loadToken(activeToken)} disabled={loading}>
            {loading ? '刷新中' : '立即刷新'}
          </button>
          <small>同步 {opts.refreshSec}s · 最后更新 {updatedAt}</small>
        </div>
      </section>

      <section className="lp-arb-guide">
        <button type="button" className="lp-arb-guide-toggle" onClick={toggleGuide}>
          <span>新手说明 · 当前池子怎么组、收益怎么拿</span>
          <em>{guideOpen ? '收起' : '展开'}</em>
        </button>
        {guideOpen && (
          <div className="lp-arb-guide-body">
            <div className="lp-arb-guide-current">
              <b>当前对照池</b>
              <p>
                {activePair
                  ? `${guidePairLabel} · ${guideDexName} ${String(activePair.version || '').toUpperCase()} · 费率约 ${guideFee}`
                  : '先在下方选中一个池子，说明会跟着当前池切换。'}
              </p>
              <div className="lp-arb-guide-actions">
                {guidePoolUrl && (
                  <a href={guidePoolUrl} target="_blank" rel="noreferrer">看池子行情</a>
                )}
                <a href={guideAddUrl} target="_blank" rel="noreferrer">
                  去 {guideDexName} 组 LP
                </a>
              </div>
            </div>

            <ol className="lp-arb-guide-steps">
              <li>
                <strong>准备两边资产</strong>
                <p>
                  组 {guidePairLabel || 'TOKEN/报价币'} 需要同时持有「目标币」和「报价币」（常见 USDT / WBNB）。
                  钱包切到 <b>BSC</b>，留一点 BNB 付 Gas。
                </p>
              </li>
              <li>
                <strong>打开对应交易所加流动性</strong>
                <p>
                  {guideDex === 'uniswap'
                    ? '点上方「去 Uniswap 组 LP」→ 选择 BSC 网络 → 选对币种与费率档（你截图里的 1.00% 就是费率档）。'
                    : '点上方「去 PancakeSwap 组 LP」→ 选择对应 V2/V3 与交易对 → 输入两边数量。'}
                  {guideIsV3
                    ? ' V3 还要选价格区间：新手可先用较宽区间，窄区间 APR 更高但容易脱区间停收手续费。'
                    : ' V2 是全区间：不用选价格带，操作更简单，但资金利用率通常低于 V3。'}
                </p>
              </li>
              <li>
                <strong>确认比例并供应流动性</strong>
                <p>
                  按页面提示授权（Approve）两边代币，再点供应/Supply。成功后钱包会收到 LP 头寸（V3 常是 NFT 仓位；V2 是 LP Token）。
                </p>
              </li>
              <li>
                <strong>收益从哪里来、怎么拿</strong>
                <p>
                  别人在这个池子里买卖时付的手续费，会按你的份额分给你。
                  {guideDex === 'uniswap' || guideIsV3
                    ? ' Uniswap / Pancake V3：手续费通常累积在仓位里，需要到仓位页手动 Collect / 领取。'
                    : ' Pancake V2：手续费多数自动滚进池值，撤出流动性时一并拿走；若该池有 Farm，还需去农场质押 LP 领 CAKE。'}
                  看板里的「约 1h / 年化」只是按成交量估算，不是到账金额。
                </p>
              </li>
              <li>
                <strong>退出与风控</strong>
                <p>
                  想结束就去同一交易所 Remove / 减少流动性，赎回两种币。注意：币价大涨大跌会产生无偿损失；土狗池还有撤池、税率、蜜罐风险。金额先小，先跑通流程。
                </p>
              </li>
            </ol>

            <div className="lp-arb-guide-tips">
              <div>
                <b>怎么读本页数字</b>
                <p>先比 TVL 与 24h 成交，再看估算 APR。成交虚高、流动性极浅的池，纸面年化再高也难真实吃到。</p>
              </div>
              <div>
                <b>推荐新手路径</b>
                <p>先选稳定币对（如 /USDT）+ 费率档清楚的池 → 小额试组 → 观察 1～2 天手续费与 IL → 再加大。</p>
              </div>
              <div>
                <b>独角兽 vs Pancake</b>
                <p>Uniswap（独角兽）与 Pancake 都是组池平台；同一币可能两边都有池，费率、深度、换手不同，用本页方案卡对比即可。</p>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="lp-arb-mode">
        <div className="lp-arb-section-head">
          <h2>单币 / 双币说明</h2>
          <p>链上 Pancake / Uniswap 的 AMM 池本身都是「双币池」；差别在于你开仓时能不能只放一种币，以及仓位会不会自动换仓。</p>
        </div>
        <div className="lp-arb-mode-grid">
          <article className="lp-arb-mode-card dual">
            <h3>双币全区间（多为 V2）</h3>
            <ul>
              <li>开仓通常要按池子比例同时投入 TOKEN + 报价币（如 USDT/WBNB）。</li>
              <li>价格怎么走都在池子里，会持续收手续费（相对持币仍有无偿损失）。</li>
              <li>上涨时仓位被动变多报价币（像在卖币）；下跌时变多目标币（像在买币）。</li>
              <li>操作简单，适合新手先跑通流程。</li>
            </ul>
            <p className="lp-arb-mode-note">当前过滤下双币全区间池：{dualPools.length} 个</p>
            <div className="lp-arb-mode-pools">
              {dualPools.slice(0, 6).map((item) => (
                <button
                  key={`dual-${item.pairAddress}`}
                  type="button"
                  className={activePair?.pairAddress === item.pairAddress ? 'active' : ''}
                  onClick={() => setSelectedPair(item.pairAddress)}
                >
                  {item.pairLabel} · {item.dexBrand?.short || item.dexId} · {formatApr(item.feeApr)}
                </button>
              ))}
              {!dualPools.length && <span style={{ color: '#94a3b8', fontSize: 12 }}>暂无匹配的双币全区间池</span>}
            </div>
          </article>
          <article className="lp-arb-mode-card single">
            <h3>可单边进场（多为 V3）</h3>
            <ul>
              <li>池子仍是 TOKEN/USDT 这类双币池，但开仓可选：两边一起加，或只放一种币。</li>
              <li>单边进场时，区间通常在现价一侧；价格还没进区间前不会自动买卖。</li>
              <li>价格进入区间后才会被动换仓：跌进区间像买币，涨进区间像卖币。</li>
              <li>出区间后基本停收手续费，仓位暂时变成单币持有，不是每秒额外扣钱。</li>
            </ul>
            <p className="lp-arb-mode-note">当前过滤下可单边（V3）池：{singleCapablePools.length} 个 · 对应你截图里的 1.00% 等费率档</p>
            <div className="lp-arb-mode-pools">
              {singleCapablePools.slice(0, 6).map((item) => (
                <button
                  key={`single-${item.pairAddress}`}
                  type="button"
                  className={activePair?.pairAddress === item.pairAddress ? 'active' : ''}
                  onClick={() => setSelectedPair(item.pairAddress)}
                >
                  {item.pairLabel} · {item.dexBrand?.short || item.dexId} · {Number(item.feeRatePct).toFixed(2)}% · {formatApr(item.feeApr)}
                </button>
              ))}
              {!singleCapablePools.length && <span style={{ color: '#94a3b8', fontSize: 12 }}>暂无匹配的可单边池</span>}
            </div>
          </article>
        </div>
      </section>

      <section className="lp-arb-options">
        <label>
          刷新间隔
          <select value={opts.refreshSec} onChange={(event) => updateOpts({ refreshSec: Number(event.target.value) })}>
            <option value={15}>15 秒</option>
            <option value={30}>30 秒</option>
            <option value={60}>60 秒</option>
          </select>
        </label>
        <label>
          排序
          <select value={opts.sortBy} onChange={(event) => updateOpts({ sortBy: event.target.value })}>
            <option value="apr">按估算 APR</option>
            <option value="liquidity">按流动性</option>
            <option value="volume">按 24h 成交</option>
          </select>
        </label>
        <label>
          最低流动性（USD）
          <input
            type="number"
            min="0"
            step="1000"
            value={opts.minLiquidity}
            onChange={(event) => updateOpts({ minLiquidity: Number(event.target.value) || 0 })}
          />
        </label>
        <label>
          DEX 过滤
          <select value={opts.dexFilter} onChange={(event) => updateOpts({ dexFilter: event.target.value })}>
            <option value="major">主流：Pancake + Uniswap</option>
            <option value="uniswap">只看 Uniswap 独角兽</option>
            <option value="pancake">只看 Pancake</option>
            <option value="all">全部 DEX</option>
          </select>
        </label>
        <label>
          开仓方式
          <select value={opts.entryFilter} onChange={(event) => updateOpts({ entryFilter: event.target.value })}>
            <option value="all">全部（双币 + 可单边）</option>
            <option value="dual">只看双币全区间（V2）</option>
            <option value="single">只看可单边（V3）</option>
          </select>
        </label>
      </section>

      <section className="lp-arb-watch">
        <div className="lp-arb-watch-add">
          <input
            placeholder="粘贴 BSC 合约，例如 0xfe189e97832da1573e4e4ff034f4ffc3a15c7777"
            value={addInput}
            onChange={(event) => setAddInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') addToken();
            }}
          />
          <button type="button" onClick={addToken}>添加币种</button>
        </div>
        <div className="lp-arb-watch-list">
          {watchlist.map((item) => {
            const cached = cacheByToken[item.address];
            const active = item.address === activeToken;
            return (
              <button
                key={item.address}
                type="button"
                className={`lp-arb-watch-card ${active ? 'active' : ''}`}
                onClick={() => setActiveToken(item.address)}
              >
                <div>
                  <strong>{item.label || cached?.tokenSymbol || shortAddress(item.address)}</strong>
                  <span>{shortAddress(item.address)}</span>
                </div>
                <em>{cached?.pairCount != null ? `${cached.pairCount} 池` : '待加载'}</em>
                <i
                  role="presentation"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeToken(item.address);
                  }}
                >
                  ×
                </i>
              </button>
            );
          })}
        </div>
      </section>

      {message && <div className="lp-arb-message">{message}</div>}

      <section className="lp-arb-summary">
        <div>
          <span>当前币种</span>
          <strong>{data?.tokenSymbol || shortAddress(activeToken)}</strong>
          <small>{data?.tokenName || activeToken}</small>
        </div>
        <div>
          <span>价格</span>
          <strong>{formatMoney(data?.priceUsd, 6)}</strong>
          <small>市值 {formatUsd(data?.marketCap)}</small>
        </div>
        <div>
          <span>可用池子</span>
          <strong>{filteredPairs.length}</strong>
          <small>
            双币 {dualPools.length} · 可单边 {singleCapablePools.length}
            {data?.dexBreakdown?.uniswap != null ? ` · UNI ${data.dexBreakdown.uniswap}` : ''}
            {data?.dexBreakdown?.pancake != null ? ` · PCS ${data.dexBreakdown.pancake}` : ''}
          </small>
        </div>
        <div>
          <span>最优方案约 1h</span>
          <strong>{best1h != null ? formatMoney(best1h, 4) : '-'}</strong>
          <small>年化约 {formatApr(bestApr)} · 本金 ${opts.capital}</small>
        </div>
      </section>

      <section className="lp-arb-strategies">
        <div className="lp-arb-section-head">
          <h2>组 LP 方案</h2>
          <p>
            双币全区间 {dualPools.length} · 可单边 V3 {singleCapablePools.length}。
            卡片标签会标明开仓方式；数字为手续费估算。
          </p>
        </div>
        <div className="lp-arb-strategy-grid">
          {strategyCards.map((item) => {
            const earn1h = yieldOnCapital(item.feeApr, opts.capital, 1);
            const selected = activePair?.pairAddress === item.pairAddress;
            const entry = item.entryMode || getEntryMode(item);
            return (
              <button
                key={item.pairAddress}
                type="button"
                className={`lp-arb-strategy-card tone-${item.strategy?.tone || 'muted'} ${selected ? 'selected' : ''}`}
                onClick={() => setSelectedPair(item.pairAddress)}
              >
                <header>
                  <b>{item.pairLabel}</b>
                  <span className="lp-arb-dex-meta">
                    <em className={`lp-arb-entry-pill ${entry.id === 'dual' ? 'dual' : 'single'}`}>
                      {entry.label}
                    </em>
                    <em className={`lp-arb-dex-pill dex-${item.dexBrand?.id || 'other'}`}>
                      {item.dexBrand?.name || item.dexId}
                    </em>
                    <em className="lp-arb-fee-pill">{Number(item.feeRatePct).toFixed(2)}%</em>
                    <i>{item.version?.toUpperCase() || '?'}</i>
                  </span>
                </header>
                <p className="lp-arb-strategy-tag">{item.strategy?.title || '通用方案'}</p>
                <div className="lp-arb-strategy-metrics">
                  <div>
                    <span>约 1h</span>
                    <strong>{earn1h != null ? formatMoney(earn1h, 4) : '-'}</strong>
                  </div>
                  <div>
                    <span>年化</span>
                    <strong>{formatApr(item.feeApr)}</strong>
                  </div>
                </div>
                <footer>
                  <span>TVL {formatUsd(item.liquidityUsd)}</span>
                  <span>24h {formatUsd(item.volume24h)}</span>
                </footer>
              </button>
            );
          })}
          {!strategyCards.length && !loading && (
            <div className="lp-arb-empty">暂无符合过滤条件的池子，可降低最低流动性或调整 DEX / 开仓方式过滤。</div>
          )}
        </div>
      </section>

      {activePair && (
        <section className="lp-arb-detail">
          <div className="lp-arb-section-head">
            <h2>详细看板 · {activePair.pairLabel}</h2>
            <p>
              {(activePair.entryMode || getEntryMode(activePair)).detail}
              {' · '}
              {activePair.strategy?.hint || '选中池子后查看收益明细与无偿损失情景。'}
            </p>
          </div>

          <div className="lp-arb-detail-grid">
            <article>
              <span>估算费率</span>
              <strong>{activePair.feeRatePct?.toFixed?.(3) ?? '-'}%</strong>
              <small>{activePair.feeEstimated ? '估算档位' : '链上费率'}</small>
            </article>
            <article>
              <span>约 1h 收益</span>
              <strong>{formatMoney(yieldOnCapital(activePair.feeApr, opts.capital, 1), 4)}</strong>
              <small>本金 ${opts.capital}</small>
            </article>
            <article>
              <span>约 1 日收益</span>
              <strong>{formatMoney(yieldOnCapital(activePair.feeApr, opts.capital, 24), 4)}</strong>
              <small>按 24h 成交外推</small>
            </article>
            <article>
              <span>年化 APR</span>
              <strong>{formatApr(activePair.feeApr)}</strong>
              <small>fee × vol / TVL × 365</small>
            </article>
            <article>
              <span>流动性</span>
              <strong>{formatUsd(activePair.liquidityUsd)}</strong>
              <small>24h 成交 {formatUsd(activePair.volume24h)}</small>
            </article>
            <article>
              <span>价格变化</span>
              <strong className={Number(activePair.priceChange24h) < 0 ? 'down' : 'up'}>
                {Number.isFinite(Number(activePair.priceChange24h)) ? `${Number(activePair.priceChange24h).toFixed(2)}%` : '-'}
              </strong>
              <small>1h {Number.isFinite(Number(activePair.priceChange1h)) ? `${Number(activePair.priceChange1h).toFixed(2)}%` : '-'}</small>
            </article>
          </div>

          <div className="lp-arb-links">
            {activePair.url && (
              <a href={activePair.url} target="_blank" rel="noreferrer">打开 DexScreener</a>
            )}
            {(activePair.dexBrand?.id === 'uniswap' || String(activePair.dexId).includes('uniswap')) ? (
              <a
                href={`https://app.uniswap.org/explore/pools/bnb/${activePair.pairAddress}`}
                target="_blank"
                rel="noreferrer"
              >
                打开 Uniswap 池子
              </a>
            ) : (
              <a
                href={`https://pancakeswap.finance/info/v${activePair.version === 'v3' ? '3' : '2'}/pairs/${activePair.pairAddress}`}
                target="_blank"
                rel="noreferrer"
              >
                打开 Pancake 池子
              </a>
            )}
            <a
              href={`https://bscscan.com/address/${activePair.pairAddress}`}
              target="_blank"
              rel="noreferrer"
            >
              BscScan
            </a>
          </div>

          <div className="lp-arb-il">
            <div className="lp-arb-il-head">
              <h3>无偿损失警告</h3>
              <p>
                组 LP 后若币价相对报价资产大幅波动，你的组合价值可能弱于「一直拿着两种币不组池」。
                下表用 V2 经典公式近似：IL = 2√k/(1+k)−1（k 为价格比变化）。手续费收益未必覆盖 IL。
              </p>
            </div>
            <div className="lp-arb-il-table">
              {IL_SCENARIOS.map((item) => {
                const il = impermanentLoss(item.move);
                return (
                  <div key={item.label} className="lp-arb-il-row">
                    <span>相对报价 {item.label}</span>
                    <strong className={il < -0.02 ? 'danger' : il < -0.005 ? 'warn' : ''}>
                      {il == null ? '-' : formatPct(il, 2)}
                    </strong>
                    <small>
                      {il == null
                        ? '-'
                        : `相对持币约少 ${formatMoney(Math.abs(il) * opts.capital, 2)}（本金 $${opts.capital}）`}
                    </small>
                  </div>
                );
              })}
            </div>
            <div className="lp-arb-il-note">
              <b>风险提示</b>
              <ul>
                <li>当前 24h 涨跌 {Number.isFinite(Number(activePair.priceChange24h)) ? `${Number(activePair.priceChange24h).toFixed(2)}%` : '-'}，波动越大，IL 越需要警惕。</li>
                <li>V3 窄区间在脱区间后不再收手续费，实际 APR 可能远低于估算。</li>
                <li>土狗池存在撤池、蜜罐、税率代币等风险，看板无法识别全部骗局。</li>
              </ul>
            </div>
          </div>
        </section>
      )}

      <LpAdvancedTrade
        pools={filteredPairs}
        activePair={activePair}
        tokenSymbol={data?.tokenSymbol || 'TOKEN'}
        tokenAddress={data?.token || activePair?.tokenAddress || activeToken || ''}
        defaultCapital={opts.capital}
        onSelectPair={setSelectedPair}
      />

      <section className="lp-arb-table-wrap">
        <div className="lp-arb-section-head">
          <h2>全部池子</h2>
          <p>点击行可切换详细看板。APR / 1h 均按当前过滤与本金重算。</p>
        </div>
        <div className="lp-arb-table">
          <div className="lp-arb-table-head">
            <span>交易对</span>
            <span>开仓方式</span>
            <span>约 1h</span>
            <span>年化</span>
            <span>TVL</span>
            <span>24h 量</span>
          </div>
          {filteredPairs.map((item) => {
            const earn1h = yieldOnCapital(item.feeApr, opts.capital, 1);
            const entry = item.entryMode || getEntryMode(item);
            return (
              <button
                key={item.pairAddress}
                type="button"
                className={`lp-arb-table-row ${activePair?.pairAddress === item.pairAddress ? 'active' : ''}`}
                onClick={() => setSelectedPair(item.pairAddress)}
              >
                <span>
                  <b>{item.pairLabel}</b>
                  <small>
                    <em className={`lp-arb-dex-pill compact dex-${item.dexBrand?.id || 'other'}`}>
                      {item.dexBrand?.short || item.dexId}
                    </em>
                    {' '}{item.version?.toUpperCase()} · {Number(item.feeRatePct).toFixed(2)}%
                  </small>
                </span>
                <span>
                  <em className={`lp-arb-entry-pill ${entry.id === 'dual' ? 'dual' : 'single'}`}>{entry.label}</em>
                </span>
                <span>{earn1h != null ? formatMoney(earn1h, 4) : '-'}</span>
                <span>{formatApr(item.feeApr)}</span>
                <span>{formatUsd(item.liquidityUsd)}</span>
                <span>{formatUsd(item.volume24h)}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
